// External Dependencies
import {
  type ReactNode, useEffect, useMemo,
} from 'react';
import { useField } from 'formik';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl, { type FormControlProps } from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Select, { type SelectProps } from '@mui/material/Select';
import clsx from 'clsx';
import styled from 'styled-components';

// Local Typings
interface SelectOption {
  id: string | number;
  label: string;
}
interface Props extends Omit<SelectProps, 'native'> {
  error?: boolean;
  helperText?: ReactNode;
  isLoading?: boolean;
  label: string;
  menuClasses?: string;
  name: string;
  onChange?: SelectProps['onChange'];
  options?: SelectOption[];
  required?: boolean;
  size?: 'small' | 'medium';
  variant?: FormControlProps['variant'];
}

// Local Variables
const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '&.customSelectForm': {
    margin: '8px 0',
  },
  '&.smallSelectForm': {
    margin: 0,
  },
  '.helperText': {
    margin: '8px 14px 0',
  },
  '.input': {
    [theme.breakpoints.down('md')]: {
      fontSize: '0.9em',
    },
  },
  '.label': {
    [theme.breakpoints.down('md')]: {
      fontSize: '0.9em',
    },
  },
}));

// Local Components
const LoadingComponent = (): JSX.Element => (
  <Box paddingRight={2}>
    <CircularProgress size={24} />
  </Box>
);

/*
 * This component should be used in a Formik context
 *
 */

// Component Definition
const CustomSelect = ({
  error,
  fullWidth = true,
  helperText,
  inputProps = {},
  isLoading,
  label,
  menuClasses = '',
  name,
  onChange,
  options,
  required = false,
  size,
  variant = 'filled',
  ...props
}: Props): JSX.Element => {
  const [field, meta, helpers] = useField(name);

  const hasError = Boolean(meta.error);
  const isTouched = Boolean(meta.touched);

  const optionValues = useMemo(
    () => options?.map((opt) => opt.id).join('-'),
    [options],
  );

  const updatedField = {
    ...field,
    ...(onChange && { onChange }),
  };

  // If the select is required and there is not a value set already,
  //  we automatically add the first option to the formik values
  useEffect(() => {
    if (!field.value && options?.length && required) {
      const shouldRevalidate = true;
      helpers.setValue(options[0].id, shouldRevalidate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value, options, required]);

  const id = `select-a-${label}`;

  let helperOrErrorText = helperText;

  if (isTouched && hasError) {
    helperOrErrorText = meta.error;
  }

  return (
    <StyledFormControl
      className={clsx(
        size === 'small'
          ? 'smallSelectForm'
          : 'customSelectForm',
        menuClasses,
      )}
      fullWidth={fullWidth}
      variant={variant}
    >
      <InputLabel
        className="label"
        htmlFor={id}
        id={field.name}
      >
        {label}
      </InputLabel>

      <Select
        IconComponent={isLoading ? LoadingComponent : undefined}
        MenuProps={{ disablePortal: true }}
        disabled={isLoading}
        error={error || (isTouched && hasError)}
        id="custom-select"
        inputProps={{
          'aria-label': label,
          classes: { root: 'input' },
          id,
          name,
          ...inputProps,
        }}
        key={optionValues} // this is necessary to get formik to grab value when options are async
        labelId={field.name}
        native={!inputProps?.readOnly}
        type="text"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...updatedField}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        {!required && (
          <option
            aria-label="None"
            value=""
          />
        )}
        {options?.length
          && options.map(({ id: optionId, label: optionLabel }) => (
            <option
              key={optionId}
              value={optionId}
            >
              {optionLabel}
            </option>
          ))}
      </Select>

      {helperOrErrorText && (
        <FormHelperText
          className="helperText"
          error={error || (isTouched && hasError)}
        >
          {helperOrErrorText}
        </FormHelperText>
      )}
    </StyledFormControl>
  );
};

export default CustomSelect;
